import { usePrimaryColor } from '../../branding/usePrimaryColor';
import { Tooltip } from '../../components/ui';
import type { HeatmapCell } from '../../types/dashboard';

interface UtilizationHeatmapProps {
  cells: HeatmapCell[];
  loading?: boolean;
  periodLabel?: string;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

function formatHourLabel(hour: number): string {
  if (hour === 0) return '12a';
  if (hour < 12) return `${hour}a`;
  if (hour === 12) return '12p';
  return `${hour - 12}p`;
}

function formatHourLong(hour: number): string {
  if (hour === 0) return '12am';
  if (hour < 12) return `${hour}am`;
  if (hour === 12) return '12pm';
  return `${hour - 12}pm`;
}

function getOpacity(booked: number, total: number): number {
  if (total === 0) return 0;
  const ratio = booked / total;
  if (ratio <= 0) return 0;
  if (ratio <= 0.5) return 0.25;
  if (ratio <= 0.75) return 0.5;
  if (ratio < 1) return 0.75;
  return 1;
}

export function UtilizationHeatmap({
  cells,
  loading = false,
  periodLabel,
}: UtilizationHeatmapProps) {
  const primaryColor = usePrimaryColor();

  // Loading state
  if (loading && cells.length === 0) {
    const skeletonHours = [9, 10, 11, 12, 13, 14, 15, 16];
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="h-4 w-32 bg-canvas animate-pulse rounded" />
        </div>
        <div className="px-4 pt-3 pb-4">
          <div className="grid gap-1" style={{ gridTemplateColumns: `40px repeat(${skeletonHours.length}, 1fr)` }}>
            {/* Header row */}
            <div />
            {skeletonHours.map((h) => (
              <div key={h} className="h-3 bg-canvas animate-pulse rounded" />
            ))}
            {/* Day rows */}
            {DAY_LABELS.map((day) => (
              <div key={day} className="contents">
                <div className="h-6 w-8 bg-canvas animate-pulse rounded" />
                {skeletonHours.map((h) => (
                  <div key={`${day}-${h}`} className="h-6 bg-canvas animate-pulse rounded" />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (cells.length === 0 && !loading) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b flex items-center gap-2">
          <h3 className="text-[14px] font-semibold">Capacity</h3>
          {periodLabel && (
            <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
          )}
        </div>
        <div className="px-4 pt-4 pb-3">
          <div className="text-center py-8">
            <p className="text-[11.5px] text-ink-4">
              Add provider availability to see capacity.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Build lookup map for cells
  const cellMap = new Map<string, HeatmapCell>();
  for (const cell of cells) {
    cellMap.set(`${cell.day}-${cell.hour}`, cell);
  }

  // Determine which hours have data
  const hoursWithData = new Set<number>();
  for (const cell of cells) {
    hoursWithData.add(cell.hour);
  }
  const hours = Array.from(hoursWithData).sort((a, b) => a - b);

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b flex items-center gap-2">
        <h3 className="text-[14px] font-semibold">Capacity</h3>
        {periodLabel && (
          <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
        )}
      </div>

      {/* Heatmap grid */}
      <div className="px-4 pt-3 pb-4 overflow-x-auto">
        <div
          className="grid gap-[3px]"
          role="grid"
          aria-label="Capacity heatmap"
          style={{ gridTemplateColumns: `40px repeat(${hours.length}, minmax(28px, 1fr))` }}
        >
          {/* Hour labels row */}
          <div />
          {hours.map((h) => (
            <div key={h} className="text-[10px] text-ink-4 text-center pb-1">
              {formatHourLabel(h)}
            </div>
          ))}

          {/* Day rows */}
          {DAY_LABELS.map((dayLabel, dayIdx) => (
            <div key={dayLabel} className="contents" role="row">
              <div className="text-[11px] text-ink-3 flex items-center h-6">
                {dayLabel}
              </div>
              {hours.map((h) => {
                const cell = cellMap.get(`${dayIdx}-${h}`);
                const opacity = cell ? getOpacity(cell.booked, cell.total) : 0;
                const tooltipText = cell
                  ? `${dayLabel} ${formatHourLong(h)}: ${cell.booked}/${cell.total} booked`
                  : null;
                const ariaLabel = cell
                  ? `${dayLabel} ${formatHourLong(h)}: ${cell.booked} of ${cell.total} booked`
                  : `${dayLabel} ${formatHourLong(h)}: no availability`;

                return (
                  <Tooltip key={`${dayIdx}-${h}`} label={tooltipText} side="top">
                    <span
                      className="block h-6 w-full rounded-sm transition-colors"
                      tabIndex={0}
                      role="gridcell"
                      aria-label={ariaLabel}
                      style={
                        opacity > 0
                          ? { backgroundColor: primaryColor, opacity }
                          : { backgroundColor: 'var(--color-canvas)' }
                      }
                    />
                  </Tooltip>
                );
              })}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-2 mt-3 pt-2 hair-t">
          <span className="text-[10px] text-ink-4">Less</span>
          {[0, 0.25, 0.5, 0.75, 1].map((op) => (
            <span
              key={op}
              className="block h-3 w-3 rounded-sm"
              style={
                op > 0
                  ? { backgroundColor: primaryColor, opacity: op }
                  : { backgroundColor: 'var(--color-canvas)' }
              }
            />
          ))}
          <span className="text-[10px] text-ink-4">More</span>
        </div>
      </div>
    </div>
  );
}
